// src/components/attendance/AttendanceManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import { attendanceService } from '../../services/attendanceService';
import { premisesService } from '../../services/premisesService';
import { staffService } from '../../services/staffService';
import { organizationService } from '../../services/organizationService';
import { useAuth } from '../../hooks/useAuth';
import AttendanceTable from './AttendanceTable';
import MarkAttendance from './MarkAttendance';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchableSelect from '../common/SearchableSelect';
import { usePageTitle } from '../../hooks/usePageTitle';

const AttendanceManagement = () => {
    usePageTitle('Attendance');
    const { user } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [premises, setPremises] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMarkAttendance, setShowMarkAttendance] = useState(false);

    // Filter states - Set premises_id based on user role
    const [filters, setFilters] = useState({
        date_from: new Date().toISOString().split('T')[0],
        date_to: new Date().toISOString().split('T')[0],
        organization_id: user?.user_type === 'client_admin' || user?.user_type === 'app_admin' ? user?.org_id || '' : '',
        premises_id: user?.user_type === 'app_admin' ? user?.premises_id || '' : '',
        user_id: '',
        status: '',
        attendance_type: '',
        page: 1,
        limit: 25
    });

    // Statistics
    const [stats, setStats] = useState({
        total_records: 0,
        total_present: 0,
        total_absent: 0,
        total_late: 0,
        total_on_leave: 0
    });

    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 25,
        total: 0,
        total_pages: 1
    });

    // Load data on component mount and filter changes
    useEffect(() => {
        loadAttendance();
        if (user?.user_type === 'core_admin') {
            loadOrganizations();
        }
        if (user?.user_type !== 'staff') {
            loadStaff();
        }
    }, [filters, user]);

    useEffect(() => {
        loadAttendanceStats();
    }, [filters.date_from, filters.date_to, filters.organization_id, filters.premises_id]);

    const loadAttendance = async () => {
        try {
            setLoading(true);
            const response = await attendanceService.getAttendance(filters);
            setAttendance(response.attendance);
            setPagination(response.pagination);
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Error loading attendance:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadPremises = async (organizationId = '') => {
        try {
            let response;
            if (user?.user_type === 'core_admin') {
                response = await premisesService.getPremisesComplete({
                    organization_id: organizationId || ''
                }, true);
            } else {
                response = await premisesService.getPremisesComplete({});
            }
            setPremises(response.premises || []);
        } catch (err) {
            console.error('Error loading premises:', err);
        }
    };

    const loadOrganizations = async () => {
        try {
            const response = await organizationService.getOrganizations({ page: 1, limit: 1000 });
            setOrganizations(response.organizations || []);
        } catch (err) {
            console.error('Error loading organizations:', err);
        }
    };

    const loadStaff = async () => {
        try {
            const apiParams = { limit: 100 };
            // App admin can only see staff from their premises
            if (user?.user_type === 'app_admin') {
                apiParams.premises_id = user.premises_id;
            }
            const response = await staffService.getStaff(apiParams);
            setStaff(response.staff);
        } catch (err) {
            console.error('Error loading staff:', err);
        }
    };

    const loadAttendanceStats = async () => {
        try {
            const response = await attendanceService.getAttendanceStats({
                date_from: filters.date_from,
                date_to: filters.date_to,
                organization_id: filters.organization_id,
                premises_id: filters.premises_id,
                attendance_type: filters.attendance_type
            });
            setStats(response);
        } catch (err) {
            console.error('Error loading attendance stats:', err);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const next = {
                ...prev,
                [key]: value,
                page: 1
            };

            // Changing premises invalidates previously selected staff option.
            if (key === 'premises_id') {
                next.user_id = '';
            }

            // Changing organization should also reset dependent filters.
            if (key === 'organization_id') {
                next.premises_id = user?.user_type === 'app_admin' ? user?.premises_id || '' : '';
                next.user_id = '';
            }

            return next;
        });
    };

    useEffect(() => {
        loadPremises(filters.organization_id);
    }, [filters.organization_id, user?.user_type]);

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handleMarkAttendance = async (attendanceData) => {
        try {
            await attendanceService.markAttendance(attendanceData);
            setShowMarkAttendance(false);
            await loadAttendance();
            await loadAttendanceStats();
            alert('Attendance marked successfully!');
        } catch (err) {
            alert('Error marking attendance: ' + err.message);
        }
    };

    const handleUpdateAttendance = async (attendanceId, attendanceData) => {
        try {
            await attendanceService.updateAttendance(attendanceId, attendanceData);
            await loadAttendance();
            await loadAttendanceStats();
            alert('Attendance updated successfully!');
        } catch (err) {
            alert('Error updating attendance: ' + err.message);
        }
    };

    const handleDeleteAttendance = async (attendanceId) => {
        if (!window.confirm('Are you sure you want to delete this attendance record?')) {
            return;
        }

        try {
            await attendanceService.deleteAttendance(attendanceId);
            await loadAttendance();
            await loadAttendanceStats();
            alert('Attendance record deleted successfully!');
        } catch (err) {
            alert('Error deleting attendance: ' + err.message);
        }
    };

    const handleExport = async (format) => {
        try {
            await attendanceService.exportAttendance(format, filters);
            const labels = { summary: 'Summary CSV', summary_pdf: 'Summary PDF' };
            const label = labels[format] || format.toUpperCase();
            alert(`Attendance data exported as ${label} successfully!`);
        } catch (err) {
            alert('Error exporting data: ' + err.message);
        }
    };

    const clearFilters = () => {
        setFilters({
            date_from: new Date().toISOString().split('T')[0],
            date_to: new Date().toISOString().split('T')[0],
            organization_id: user?.user_type === 'client_admin' || user?.user_type === 'app_admin' ? user?.org_id || '' : '',
            premises_id: user?.user_type === 'app_admin' ? user?.premises_id || '' : '',
            user_id: '',
            status: '',
            attendance_type: '',
            page: 1,
            limit: 25
        });
    };

    // Calculate attendance percentage
    const calculateAttendancePercentage = () => {
        if (stats.total_records === 0) return 0;
        return ((stats.total_present / stats.total_records) * 100).toFixed(1);
    };

    const filteredStaff = useMemo(() => {
        let list = Array.isArray(staff) ? [...staff] : [];
        if (filters.organization_id) {
            list = list.filter(member => String(member.org_id) === String(filters.organization_id));
        }
        if (filters.premises_id) {
            list = list.filter(member => String(member.premises_id) === String(filters.premises_id));
        }
        return list;
    }, [staff, filters.organization_id, filters.premises_id]);

    const organizationOptions = useMemo(() => (
        (organizations || []).map((org) => ({
            id: String(org.id),
            name: org.name || org.organization_name || `Organization ${org.id}`
        }))
    ), [organizations]);

    const premisesOptions = useMemo(() => (
        (premises || [])
            .filter((premise) => {
                if (!filters.organization_id) return true;
                const premiseOrgId = premise.org_id ?? premise.organization_id ?? '';
                return String(premiseOrgId) === String(filters.organization_id);
            })
            .map((premise) => ({
                id: String(premise.id),
                name: premise.premises_name || premise.name || `Premises ${premise.id}`
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    ), [premises, filters.organization_id]);

    const staffOptions = useMemo(() => (
        (filteredStaff || [])
            .map((member) => ({
                id: String(member.user_id),
                name: `${member.name || member.username || 'Staff'} (${member.staff_id || 'N/A'})`
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    ), [filteredStaff]);

    if (loading && attendance.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="attendance-management">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <i className="fas fa-calendar-check"></i>
                        </div>
                        <div className="header-text">
                            <h1>Attendance Management</h1>
                            <p>Track and manage staff attendance records</p>
                        </div>
                    </div>
                </div>

                {/* Attendance Statistics */}
                <div className="stats-grid">
                    <div className="stat-card stat-present">
                        <div className="stat-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.total_present}</div>
                            <div className="stat-label">Present</div>
                        </div>
                    </div>

                    <div className="stat-card stat-absent">
                        <div className="stat-icon">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.total_absent}</div>
                            <div className="stat-label">Absent</div>
                        </div>
                    </div>

                    <div className="stat-card stat-late">
                        <div className="stat-icon">
                            <i className="fas fa-clock"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.total_late}</div>
                            <div className="stat-label">Late Arrivals</div>
                        </div>
                    </div>

                    <div className="stat-card stat-leave">
                        <div className="stat-icon">
                            <i className="fas fa-umbrella-beach"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.total_on_leave}</div>
                            <div className="stat-label">On Leave</div>
                        </div>
                    </div>

                    <div className="stat-card stat-percentage">
                        <div className="stat-icon">
                            <i className="fas fa-percentage"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{calculateAttendancePercentage()}%</div>
                            <div className="stat-label">Attendance Rate</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-section">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Filter Records
                        </h3>
                        <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                            <i className="fas fa-redo"></i>
                            Reset
                        </button>
                    </div>

                    <div className="attendance-type-quick-filter">
                        <span className="quick-filter-label">Attendance Location:</span>
                        <button
                            type="button"
                            className={`type-chip ${filters.attendance_type === '' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('attendance_type', '')}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            className={`type-chip ${filters.attendance_type === 'in_premises' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('attendance_type', 'in_premises')}
                        >
                            In-Premises
                        </button>
                        <button
                            type="button"
                            className={`type-chip ${filters.attendance_type === 'offsite' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('attendance_type', 'offsite')}
                        >
                            Offsite
                        </button>
                    </div>

                    <div className="filters-grid">
                        {user?.user_type === 'core_admin' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-sitemap"></i>
                                    Organization
                                </label>
                                <SearchableSelect
                                    options={organizationOptions}
                                    value={filters.organization_id}
                                    onChange={(value) => handleFilterChange('organization_id', value)}
                                    placeholder="All Organizations"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search organizations..."
                                    className="custom-select"
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-calendar-day"></i>
                                From Date
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={filters.date_from}
                                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-calendar-day"></i>
                                To Date
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={filters.date_to}
                                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                            />
                        </div>

                        {/* Only show premises filter if not app_admin */}
                        {user?.user_type !== 'app_admin' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-building"></i>
                                    Premises
                                </label>
                                <SearchableSelect
                                    options={premisesOptions}
                                    value={filters.premises_id}
                                    onChange={(value) => handleFilterChange('premises_id', value)}
                                    placeholder="All Premises"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search premises..."
                                    className="custom-select"
                                />
                            </div>
                        )}

                        {user?.user_type !== 'staff' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-user"></i>
                                    Staff Member
                                </label>
                                <SearchableSelect
                                    options={staffOptions}
                                    value={filters.user_id}
                                    onChange={(value) => handleFilterChange('user_id', value)}
                                    placeholder="All Staff"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search staff..."
                                    className="custom-select"
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-map-marker-alt"></i>
                                Attendance Location
                            </label>
                            <select
                                className="form-input"
                                value={filters.attendance_type}
                                onChange={(e) => handleFilterChange('attendance_type', e.target.value)}
                            >
                                <option value="">All Locations</option>
                                <option value="in_premises">In-Premises</option>
                                <option value="offsite">Offsite</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-info-circle"></i>
                                Status
                            </label>
                            <select
                                className="form-input"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                                <option value="late">Late</option>
                                <option value="on_leave">On Leave</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>{error}</span>
                    </div>
                )}

                {/* Attendance Table */}
                <AttendanceTable
                    attendance={attendance}
                    loading={loading}
                    pagination={pagination}
                    onUpdate={handleUpdateAttendance}
                    onDelete={handleDeleteAttendance}
                    onPageChange={handlePageChange}
                    onExport={handleExport}
                    onRefresh={async () => {
                        await loadAttendance();
                        await loadAttendanceStats();
                    }}
                    userType={user?.user_type}
                />

                {/* Mark Attendance Modal */}
                <Modal
                    show={showMarkAttendance}
                    onClose={() => setShowMarkAttendance(false)}
                    title="Mark Attendance"
                    size="medium"
                >
                    <MarkAttendance
                        premises={premises}
                        onSubmit={handleMarkAttendance}
                        onCancel={() => setShowMarkAttendance(false)}
                        userType={user?.user_type}
                    />
                </Modal>
            </div>

            <style jsx>{`
        .attendance-management {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 2rem 0;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .page-header {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-left: 5px solid #667eea;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }

        .header-text h1 {
          color: #2d3748;
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
        }

        .header-text p {
          color: #718096;
          margin: 0;
          font-size: 1rem;
        }

        .btn-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 0.8rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
          transition: all 0.3s ease;
        }

        .btn-gradient:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          gap: 1.2rem;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-card.stat-present {
          border-left-color: #10b981;
        }

        .stat-card.stat-present .stat-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .stat-card.stat-absent {
          border-left-color: #ef4444;
        }

        .stat-card.stat-absent .stat-icon {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }

        .stat-card.stat-late {
          border-left-color: #f59e0b;
        }

        .stat-card.stat-late .stat-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .stat-card.stat-leave {
          border-left-color: #3b82f6;
        }

        .stat-card.stat-leave .stat-icon {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }

        .stat-card.stat-percentage {
          border-left-color: #8b5cf6;
        }

        .stat-card.stat-percentage .stat-icon {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .stat-details {
          flex: 1;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
          line-height: 1;
          margin-bottom: 0.3rem;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #718096;
          font-weight: 500;
        }

        .filters-section {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .filters-header h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }

        .filters-header h3 i {
          color: #667eea;
        }

        .btn-ghost {
          background: transparent;
          border: 2px solid #e2e8f0;
          color: #718096;
          transition: all 0.3s ease;
        }

        .btn-ghost:hover {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .filters-grid {
          display: flex;
          gap: 1.5rem;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .attendance-type-quick-filter {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .quick-filter-label {
          font-weight: 600;
          color: #2d3748;
          margin-right: 0.25rem;
        }

        .type-chip {
          border: 1px solid #d1d5db;
          background: #fff;
          color: #374151;
          padding: 0.4rem 0.85rem;
          border-radius: 999px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .type-chip.active {
          border-color: #2563eb;
          background: #2563eb;
          color: #fff;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          flex: 1;
          min-width: 0;
        }

        .filter-group label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label i {
          color: #667eea;
          font-size: 0.85rem;
        }

        .form-input {
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          border-left: 4px solid #dc2626;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .header-content {
            flex-direction: column;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </div>
    );
};

export default AttendanceManagement;
